import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faChevronLeft, faSearch } from "@fortawesome/free-solid-svg-icons";

/**
 * Dual-pane lock shuttle selector.
 *
 * Props:
 *   allLocks   – [{assetUnitId, name}] – all GPS locks available to this client
 *                (already filtered to exclude locks on OTHER active trips)
 *   assigned   – string[]              – assetUnitIds currently assigned to THIS trip
 *   onChange   – (string[]) => void   – called with new assigned list on every change
 *   disabled   – bool                 – disable all interaction
 *   loading    – bool                 – show loading state
 */
const LockShuttle = ({ allLocks = [], assigned = [], onChange, disabled = false, loading = false }) => {
    const [leftSearch, setLeftSearch]   = useState("");
    const [rightSearch, setRightSearch] = useState("");
    const [leftChecked, setLeftChecked]   = useState(new Set());
    const [rightChecked, setRightChecked] = useState(new Set());

    const assignedSet = useMemo(() => new Set(assigned), [assigned]);

    const leftLocks = useMemo(() =>
        allLocks.filter(l =>
            !assignedSet.has(l.assetUnitId) &&
            (l.name || l.assetUnitId).toLowerCase().includes(leftSearch.toLowerCase())
        ), [allLocks, assignedSet, leftSearch]);

    const rightLocks = useMemo(() =>
        allLocks.filter(l =>
            assignedSet.has(l.assetUnitId) &&
            (l.name || l.assetUnitId).toLowerCase().includes(rightSearch.toLowerCase())
        ), [allLocks, assignedSet, rightSearch]);

    const toggleLeft  = (id) => setLeftChecked(prev  => { const s = new Set(prev);  s.has(id) ? s.delete(id) : s.add(id); return s; });
    const toggleRight = (id) => setRightChecked(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

    const selectAllLeft  = () => setLeftChecked(new Set(leftLocks.map(l => l.assetUnitId)));
    const selectAllRight = () => setRightChecked(new Set(rightLocks.map(l => l.assetUnitId)));
    const clearLeft  = () => setLeftChecked(new Set());
    const clearRight = () => setRightChecked(new Set());

    const addSelected = () => {
        if (leftChecked.size === 0) return;
        onChange([...assigned, ...Array.from(leftChecked)]);
        setLeftChecked(new Set());
    };

    const removeSelected = () => {
        if (rightChecked.size === 0) return;
        onChange(assigned.filter(id => !rightChecked.has(id)));
        setRightChecked(new Set());
    };

    const addAll  = () => { onChange([...new Set([...assigned, ...leftLocks.map(l => l.assetUnitId)])]); setLeftChecked(new Set()); };
    const removeAll = () => { onChange(assigned.filter(id => !rightLocks.some(l => l.assetUnitId === id))); setRightChecked(new Set()); };

    const paneStyle = {
        flex: 1, border: "1.5px solid #e0e0e0", borderRadius: "8px",
        display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff",
        minHeight: "280px", maxHeight: "340px",
    };
    const headerStyle = {
        padding: "8px 12px", background: "#f8f9fa", borderBottom: "1px solid #e0e0e0",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        fontSize: "12px", fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: "0.05em",
    };
    const listStyle = { overflowY: "auto", flex: 1, padding: "4px 0" };
    const itemStyle = (checked) => ({
        display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px",
        cursor: "pointer", fontSize: "13px", color: "#333",
        background: checked ? "#e8f0fe" : "transparent",
        transition: "background 0.1s",
    });

    if (loading) {
        return (
            <div style={{ padding: "32px", textAlign: "center", color: "#888", border: "1.5px solid #e0e0e0", borderRadius: "8px" }}>
                Loading GPS locks…
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: "flex", gap: "12px", alignItems: "stretch" }}>
                {/* LEFT – available */}
                <div style={paneStyle}>
                    <div style={headerStyle}>
                        <span>Available ({leftLocks.length})</span>
                        <div style={{ display: "flex", gap: "6px" }}>
                            <button type="button" onClick={selectAllLeft} disabled={disabled || leftLocks.length === 0}
                                style={{ fontSize: "11px", background: "none", border: "none", color: "#1a73e8", cursor: "pointer", padding: 0 }}>
                                All
                            </button>
                            <span style={{ color: "#ccc" }}>|</span>
                            <button type="button" onClick={clearLeft} disabled={disabled || leftChecked.size === 0}
                                style={{ fontSize: "11px", background: "none", border: "none", color: "#888", cursor: "pointer", padding: 0 }}>
                                None
                            </button>
                        </div>
                    </div>
                    <div style={{ padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>
                        <div style={{ position: "relative" }}>
                            <FontAwesomeIcon icon={faSearch} style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: "11px" }} />
                            <input
                                type="text" placeholder="Search…" value={leftSearch}
                                onChange={e => setLeftSearch(e.target.value)}
                                style={{ width: "100%", paddingLeft: "26px", paddingRight: "8px", height: "28px", border: "1px solid #e0e0e0", borderRadius: "4px", fontSize: "12px", boxSizing: "border-box" }}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                    <div style={listStyle}>
                        {leftLocks.length === 0
                            ? <div style={{ padding: "16px", textAlign: "center", color: "#aaa", fontSize: "12px" }}>No locks available</div>
                            : leftLocks.map(l => {
                                const checked = leftChecked.has(l.assetUnitId);
                                return (
                                    <div key={l.assetUnitId} style={itemStyle(checked)}
                                        onClick={() => !disabled && toggleLeft(l.assetUnitId)}>
                                        <input type="checkbox" checked={checked} onChange={() => {}} style={{ cursor: "pointer" }} />
                                        <span>{l.name || l.assetUnitId}</span>
                                        {l.name && <span style={{ color: "#aaa", fontSize: "11px", marginLeft: "auto" }}>{l.assetUnitId}</span>}
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>

                {/* MIDDLE – action buttons */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "8px", minWidth: "88px" }}>
                    <button type="button" onClick={addSelected} disabled={disabled || leftChecked.size === 0}
                        className="btn-sm btn-primary" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                        <FontAwesomeIcon icon={faChevronRight} /> Add ({leftChecked.size})
                    </button>
                    <button type="button" onClick={addAll} disabled={disabled || leftLocks.length === 0}
                        className="btn-sm btn-primary" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                        <FontAwesomeIcon icon={faChevronRight} /><FontAwesomeIcon icon={faChevronRight} /> Add All
                    </button>
                    <button type="button" onClick={removeSelected} disabled={disabled || rightChecked.size === 0}
                        className="btn-sm btn-secondary" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                        <FontAwesomeIcon icon={faChevronLeft} /> Remove ({rightChecked.size})
                    </button>
                    <button type="button" onClick={removeAll} disabled={disabled || rightLocks.length === 0}
                        className="btn-sm btn-secondary" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                        <FontAwesomeIcon icon={faChevronLeft} /><FontAwesomeIcon icon={faChevronLeft} /> Remove All
                    </button>
                </div>

                {/* RIGHT – assigned */}
                <div style={paneStyle}>
                    <div style={headerStyle}>
                        <span>Assigned ({rightLocks.length})</span>
                        <div style={{ display: "flex", gap: "6px" }}>
                            <button type="button" onClick={selectAllRight} disabled={disabled || rightLocks.length === 0}
                                style={{ fontSize: "11px", background: "none", border: "none", color: "#1a73e8", cursor: "pointer", padding: 0 }}>
                                All
                            </button>
                            <span style={{ color: "#ccc" }}>|</span>
                            <button type="button" onClick={clearRight} disabled={disabled || rightChecked.size === 0}
                                style={{ fontSize: "11px", background: "none", border: "none", color: "#888", cursor: "pointer", padding: 0 }}>
                                None
                            </button>
                        </div>
                    </div>
                    <div style={{ padding: "6px 8px", borderBottom: "1px solid #f0f0f0" }}>
                        <div style={{ position: "relative" }}>
                            <FontAwesomeIcon icon={faSearch} style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", color: "#bbb", fontSize: "11px" }} />
                            <input
                                type="text" placeholder="Search…" value={rightSearch}
                                onChange={e => setRightSearch(e.target.value)}
                                style={{ width: "100%", paddingLeft: "26px", paddingRight: "8px", height: "28px", border: "1px solid #e0e0e0", borderRadius: "4px", fontSize: "12px", boxSizing: "border-box" }}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                    <div style={listStyle}>
                        {rightLocks.length === 0
                            ? <div style={{ padding: "16px", textAlign: "center", color: "#aaa", fontSize: "12px" }}>No locks assigned</div>
                            : rightLocks.map(l => {
                                const checked = rightChecked.has(l.assetUnitId);
                                return (
                                    <div key={l.assetUnitId} style={itemStyle(checked)}
                                        onClick={() => !disabled && toggleRight(l.assetUnitId)}>
                                        <input type="checkbox" checked={checked} onChange={() => {}} style={{ cursor: "pointer" }} />
                                        <span>{l.name || l.assetUnitId}</span>
                                        {l.name && <span style={{ color: "#aaa", fontSize: "11px", marginLeft: "auto" }}>{l.assetUnitId}</span>}
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            </div>
            <div style={{ marginTop: "6px", fontSize: "11px", color: "#888" }}>
                {assigned.length} lock{assigned.length !== 1 ? "s" : ""} assigned in total
            </div>
        </div>
    );
};

export default LockShuttle;
